import React, { useState, useEffect } from "react";
import api from "../apiClient";
import TrialNotificationBanner from "./TrialNotificationBanner";
import "./Modal.css";

export default function LicenseManagement({ licenseType, displayName, onClose }) {
  const [licenseCount, setLicenseCount] = useState(0);
  const [licensedEntities, setLicensedEntities] = useState([]);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [updateCount, setUpdateCount] = useState(1);
  const [pendingUpdate, setPendingUpdate] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [lastChange, setLastChange] = useState(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [trialEnd, setTrialEnd] = useState(null);
  const [isInTrial, setIsInTrial] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [pausedAt, setPausedAt] = useState(null);
  const [resumesAt, setResumesAt] = useState(null);
  const [status, setStatus] = useState(null);
  const [licenseData, setLicenseData] = useState(null);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);

  const fetchLicense = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/company/licenses/${licenseType}`);
      const data = res.data;

      setLicenseData(data);
      setLicenseCount(data.licenseCount || 0);
      setLicensedEntities(data.licensedEntities || []);
      setUpdateCount(data.licenseCount || 1);
      setSubscriptionActive(!!data.active);
      setStatus(data.status);
      setIsPaused(data.isPaused || false);
      setHasUsedTrial(data.hasUsedTrial || false); // NEW - Add this line

      if (data.status === 'trialing' && data.trialEnd) {
        const trialEndDate = new Date(data.trialEnd);
        setTrialEnd(trialEndDate);
        setIsInTrial(new Date() < trialEndDate);
      } else {
        setTrialEnd(null);
        setIsInTrial(false);
      }

      if (data.pausedAt) {
        setPausedAt(new Date(data.pausedAt));
      }
      if (data.resumesAt) {
        setResumesAt(new Date(data.resumesAt));
      }

      if (data.lastQuantityChange) {
        const last = new Date(data.lastQuantityChange);
        setLastChange(last);

        const now = new Date();
        const cooldownMs = 24 * 60 * 60 * 1000;
        const diff = cooldownMs - (now - last);
        if (diff > 0) {
          setCooldownRemaining(Math.ceil(diff / (60 * 1000)));
        } else {
          setCooldownRemaining(null);
        }
      }
    } catch (e) {
      setError("Failed to load license data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLicense();
  }, [licenseType]);

  const getDaysRemainingInTrial = () => {
    if (!trialEnd) return 0;
    const now = new Date();
    const diff = trialEnd - now;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const handleSubscribe = async () => {
    setError("");
    try {
      const res = await api.post(`/api/company/licenses/${licenseType}/subscribe`, {
        count: updateCount,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to start subscription");
    }
  };

  const handleUpdateSubscription = async () => {
    setPendingUpdate(true);
    setError("");
    setSuccess("");
    setLicenseCount(updateCount);

    try {
      await api.post(`/api/company/licenses/${licenseType}/update`, {
        count: updateCount,
      });
      setSuccess("Subscription update initiated");
      setShowConfirmModal(false);
      setTimeout(fetchLicense, 2000);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update subscription");
      fetchLicense();
    } finally {
      setPendingUpdate(false);
    }
  };

  const handlePauseSubscription = async () => {
    setPendingUpdate(true);
    setError("");
    setSuccess("");

    try {
      await api.post(`/api/company/licenses/${licenseType}/pause`);
      setSuccess("Subscription paused successfully");
      setShowPauseModal(false);
      setTimeout(fetchLicense, 1000);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to pause subscription");
    } finally {
      setPendingUpdate(false);
    }
  };

  const handleResumeSubscription = async () => {
    setPendingUpdate(true);
    setError("");
    setSuccess("");

    try {
      await api.post(`/api/company/licenses/${licenseType}/resume`);
      setSuccess("Subscription resumed successfully");
      setTimeout(fetchLicense, 1000);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to resume subscription");
    } finally {
      setPendingUpdate(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel this subscription? It will remain active until the end of the billing period.")) {
      return;
    }

    setPendingUpdate(true);
    setError("");
    setSuccess("");

    try {
      await api.post(`/api/company/licenses/${licenseType}/cancel`);
      setSuccess("Subscription will be canceled at the end of the billing period");
      setTimeout(fetchLicense, 1000);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to cancel subscription");
    } finally {
      setPendingUpdate(false);
    }
  };

  return (
    <div className="management-overlay">
      <div className="management-modal">
        <div className="modal-header">
          <h2>{displayName} License Management</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {/* ADD THIS: Trial Notification Banner */}
          <TrialNotificationBanner 
            licenseType={licenseType}
            licenseData={licenseData}
            onDismiss={fetchLicense}
          />

          {isInTrial && (
            <div className="alert alert-info" style={{ marginBottom: '20px' }}>
              <strong>Free Trial Active!</strong> You have {getDaysRemainingInTrial()} days remaining. 
              You won't be charged until {trialEnd?.toLocaleDateString()}.
            </div>
          )}

          {isPaused && (
            <div className="alert alert-warning" style={{ marginBottom: '20px' }}>
              <strong>Subscription Paused</strong> - Your subscription is currently paused. 
              {pausedAt && ` Paused on ${pausedAt.toLocaleDateString()}.`}
            </div>
          )}

          <section className="section">
            <h3>{subscriptionActive ? "Manage Subscription" : "Subscribe"}</h3>

            {subscriptionActive ? (
              <div className="form-group">
                <label>Current Subscribed Quantity:</label>
                <p><strong>{licenseCount}</strong></p>
                <p style={{ fontSize: '0.9em', color: '#666' }}>
                  Status: <strong>{status === 'trialing' ? 'Free Trial' : status === 'paused' ? 'Paused' : 'Active'}</strong>
                </p>

                
                {!isPaused && (
                  <>
                    <label>Update License Quantity:</label>
                    <input
                      type="number"
                      min={1}
                      value={updateCount}
                      onChange={(e) => setUpdateCount(Number(e.target.value))}
                    />
                    {cooldownRemaining && updateCount < licenseCount ? (
                      <button className="btn btn-disabled" disabled>
                        Downgrade allowed in ~{Math.ceil(cooldownRemaining / 60)}h
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary"
                        onClick={() => setShowConfirmModal(true)}
                        disabled={pendingUpdate}
                      >
                        {pendingUpdate ? "Updating..." : "Update Subscription"}
                      </button>
                    )}
                    {cooldownRemaining && updateCount < licenseCount && (
                      <p style={{ fontSize: '0.9em', color: '#666', marginTop: '8px' }}>
                        Downgrades have a 24-hour cooldown. Upgrades can be done anytime.
                      </p>
                    )}
                  </>
                )}

                <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {isPaused ? (
                    <button
                      className="btn btn-primary"
                      onClick={handleResumeSubscription}
                      disabled={pendingUpdate}
                    >
                      {pendingUpdate ? "Resuming..." : "Resume Subscription"}
                    </button>
                  ) : (
                    <button
                      className="btn btn-warning"
                      onClick={() => setShowPauseModal(true)}
                      disabled={pendingUpdate}
                    >
                      Pause Subscription
                    </button>
                  )}
                  
                  <button
                    className="btn btn-danger"
                    onClick={handleCancelSubscription}
                    disabled={pendingUpdate}
                  >
                    Cancel Subscription
                  </button>
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label>Choose Quantity:</label>
                <input
                  type="number"
                  min={1}
                  value={updateCount}
                  onChange={(e) => setUpdateCount(Number(e.target.value))}
                />
                <button className="btn btn-primary" onClick={handleSubscribe}>
                  {hasUsedTrial ? "Subscribe Now" : "Start Free Trial (30 Days)"}
                </button>
                {!hasUsedTrial && (
                  <p style={{ fontSize: '0.9em', color: '#666', marginTop: '8px' }}>
                    You won't be charged for 30 days. Cancel anytime during the trial.
                  </p>
                )}
                {hasUsedTrial && (
                  <p style={{ fontSize: '0.9em', color: '#666', marginTop: '8px' }}>
                    You've already used your free trial for this license type. Subscription will start immediately.
                  </p>
                )}
              </div>
            )}
          </section>

          <section className="section">
            <h3>Licensed Entities ({licensedEntities.length})</h3>
            <div className="table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Entity ID</th>
                  </tr>
                </thead>
                <tbody>
                  {licensedEntities.length > 0 ? (
                    licensedEntities.map((id) => (
                      <tr key={id}>
                        <td>{id}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="1">No licensed entities.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      
      {showConfirmModal && (
        <div className="confirm-overlay">
          <div className="confirm-modal">
            <h3>Confirm Subscription Update</h3>
            <p>
              Updating your subscription from <strong>{licenseCount}</strong> to{" "}
              <strong>{updateCount}</strong> licenses will {isInTrial ? 'take effect immediately' : 'immediately charge or credit your payment method on file'}.
            </p>
            {isInTrial && (
              <p style={{ fontSize: '0.9em', color: '#666' }}>
                You're currently in a free trial. You won't be charged until {trialEnd?.toLocaleDateString()}.
              </p>
            )}
            <p>Do you want to proceed?</p>
            <div className="confirm-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowConfirmModal(false)}
                disabled={pendingUpdate}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateSubscription}
                disabled={pendingUpdate}
              >
                {pendingUpdate ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPauseModal && (
        <div className="confirm-overlay">
          <div className="confirm-modal">
            <h3>Pause Subscription?</h3>
            <p>
              Pausing your subscription will:
            </p>
            <ul style={{ textAlign: 'left', marginLeft: '20px' }}>
              <li>Stop all future billing</li>
              <li>Keep your current licenses active</li>
              <li>Retain all your data</li>
              <li>Allow you to resume anytime</li>
            </ul>
            <p>Your current billing period will not be refunded.</p>
            <div className="confirm-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowPauseModal(false)}
                disabled={pendingUpdate}
              >
                Cancel
              </button>
              <button
                className="btn btn-warning"
                onClick={handlePauseSubscription}
                disabled={pendingUpdate}
              >
                {pendingUpdate ? "Pausing..." : "Pause Subscription"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}